// Third Party
import React from 'react';
import moment from 'moment';

// Components
import { SecondaryButton } from '../../buttons';
import { UnitInput, EditableInput, SelectInput, SelectOption } from '../../inputs';
import DatePicker from '../../inputs/DatePicker';
import ToggleSwitch from '../../inputs/ToggleSwitch';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { InfoTooltip } from '../../tooltips';
import { YieldParamsType } from '../../../../utils/constants';
import User from '@/types/user';
import CustomYieldParams from '@/types/custom-yield-params';
import ShelvableInstrument from '@/types/shelvable-instrument';
import CompliantInstrument from '@/types/compliant-instrument';
import Shelf from '@/types/shelf';
import Nullable from '@/types/nullable';

type Props = {
  user: User;
  title?: string;
  onSubmit: (config: CustomYieldParams, shelfInfo: ShelvableInstrument & CompliantInstrument) => void;
  benchmarkLipperId?: number;
  filteredShelves?: Shelf[];
  complianceComparisons?: string[];
};

enum TermUnit {
  Months = 'months',
  Years = 'years',
}

type State = {
  name: string;
  rate: Nullable<number>;
  term: Nullable<number>;
  termUnits: TermUnit;
  startDate: Nullable<Date>;
  automaticRenewal: boolean;
};

class QuickAddGICSimulator extends React.Component<Props, State> {
  private initialState: State = {
    name: formatMessage('EditPortfolioModal.GICSimulator.DefaultName'),
    rate: 1,
    term: 1,
    termUnits: TermUnit.Years,
    startDate: null,
    automaticRenewal: true,
  };

  readonly state: State = this.initialState;

  clearForm = () => {
    this.setState(this.initialState);
  };

  onAdd = () => {
    const { name, rate, term, termUnits, startDate, automaticRenewal } = this.state;

    if (term === null || rate === null || name.trim() === '') {
      return;
    }

    this.props.onSubmit(
      {
        name,
        rate,
        term,
        termUnits,
        startDate: startDate || moment().utc().startOf('year').startOf('day').subtract('10', 'years').startOf('day').toDate(),
        automaticRenewal,
        frequency: 'daily',
        benchmarkLipperId: this.props.benchmarkLipperId,
        type: YieldParamsType.GIC,
      },
      {
        filteredShelves: this.props.filteredShelves,
        complianceComparisons: this.props.complianceComparisons,
      },
    );

    this.clearForm();
  };

  onDateChange = (date: Date) => {
    this.setState({ startDate: date });
  };

  onRateChange = (value: string) => {
    let rate: Nullable<number> = null;

    if (value.trim() !== '') {
      rate = Number(value);
    }

    this.setState({ rate });
  };

  onTermChange = (value: string) => {
    let term: Nullable<number> = null;

    if (value.trim() !== '') {
      term = Number(value);
    }

    this.setState({ term });
  };

  onTermUnitChange = (termUnits: TermUnit) => {
    this.setState({ termUnits });
  };

  onAutomaticRenewalChange = (checked: boolean) => {
    this.setState({ automaticRenewal: checked });
  };

  onNameChange = (name: string) => {
    this.setState({ name });
  };

  getTermUnitControl = () => (
    <SelectInput onValueChange={this.onTermUnitChange} selected={this.state.termUnits}>
      <SelectOption key={TermUnit.Months} value={TermUnit.Months}>
        <FormattedMessage id="EditPortfolioModal.GICSimulator.TermUnits.Months" />
      </SelectOption>

      <SelectOption key={TermUnit.Years} value={TermUnit.Years}>
        <FormattedMessage id="EditPortfolioModal.GICSimulator.TermUnits.Years" />
      </SelectOption>
    </SelectInput>
  );

  renderSimulator = () => {
    const { name, rate, term, startDate, automaticRenewal } = this.state;

    return (
      <>
        <div className="simulator-row justify-content-between">
          <EditableInput
            compact
            data-private
            data-dd-privacy="mask"
            value={name}
            placeholder={formatMessage('EditPortfolioModal.GICSimulator.PlaceholderName')}
            onValueChange={(value: string) => this.onNameChange(value)}
            className="custom-name"
          />
        </div>

        <div className="simulator-row">
          <div className="rate-container">
            <span className="field-label">
              <FormattedMessage id="EditPortfolioModal.GICSimulator.Rate" />
            </span>

            <UnitInput compact name="simulator-rate" type="number" min={0} value={rate === null ? '' : rate} unitSymbol="%" onValueChange={this.onRateChange} />
          </div>

          <div className="term-container">
            <span className="field-label">
              <FormattedMessage id="EditPortfolioModal.GICSimulator.Term" />
            </span>

            <UnitInput
              compact
              name="simulator-term"
              type="number"
              min={0}
              value={term === null ? '' : term}
              unitSymbol={this.getTermUnitControl()}
              onValueChange={this.onTermChange}
            />
          </div>

          <div className="date-container">
            <span className="field-label">
              <FormattedMessage id="EditPortfolioModal.GICSimulator.StartDate" />
            </span>

            <DatePicker compact allowPrevNext2 onDateChange={(date: Date) => this.onDateChange(date)} defaultDate={startDate} />
            <InfoTooltip message={formatMessage('EditPortfolioModal.GICSimulator.StartDateTooltip')} />
          </div>
        </div>

        <div className="simulator-row">
          <ToggleSwitch onChange={this.onAutomaticRenewalChange} checked={automaticRenewal} />

          <span className="field-label">
            <FormattedMessage id="EditPortfolioModal.GICSimulator.AutomaticRenewal" />
          </span>
        </div>

        <div className="simulator-row justify-content-end">
          <SecondaryButton compact className="add-btn" onClick={this.onAdd}>
            <span>{formatMessage('EditPortfolioModal.GICSimulator.AddToPortfolio')}</span>
          </SecondaryButton>
        </div>
      </>
    );
  };

  render() {
    const { title } = this.props;

    return (
      <div className="simulator-section">
        {title && <span className="simulator-section-title">{title}</span>}

        <div className="d-flex simulator-wrapper">{this.renderSimulator()}</div>
      </div>
    );
  }
}

export default QuickAddGICSimulator;
